import { useState, useEffect } from "react";
import { classNames } from "primereact/utils";
import { Button } from "primereact/button";
import {
  ForumTopicEnum,
  ForumWithoutMessage,
  getColorForLetter,
  ToggleVisibilityDto
} from "../../services/forum/forum.dto";
import forumService from "../../services/forum/forum.service";
import { useToast } from "../../utils/toast";
import { DataView } from "primereact/dataview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Fieldset } from "primereact/fieldset";
import { CardDataDto, cardData } from "./ForumHomePage";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../utils/auth";
import { Role } from "../../services/auth/auth.dto";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { BreadCrumb } from "primereact/breadcrumb";
import { MenuItem } from "primereact/menuitem";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

interface Mesaj {
  mesaj: string;
  mesaj_kimden: string;
}

interface Topic {
  forum_header: string;
  message_content: string;
  forum_topic: ForumTopicEnum;
  mesajlar: Mesaj[];
  creatorName: string;
  creation_date: string;
  view_count: number;
  replies: number;
}

const ForumlarPage = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState(location.state?.searchTerm || "");
  const topicIdNumber = Number(topicId);
  const toast = useToast();
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState<CardDataDto>(cardData[0]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const auth = useAuth();
  const items: MenuItem[] = [
    {
      label: "Forum",
      command: () => {
        navigate("/app/forum-homepage");
      }
    },
    { label: cardData[topicIdNumber - 1].title }
  ];
  const home: MenuItem = {
    icon: "pi pi-home",
    command: () => {
      navigate("/app/forum-homepage");
    }
  };

  const [sortField, setSortField] = useState("creation-date");
  const [sortOrder, setSortOrder] = useState(1);
  const [sortOption, setSortOption] = useState("creation-date_asc");

  const sortOptions = [
    { label: "En Yeni", value: "creation-date_asc" },
    { label: "En Eski", value: "creation-date_desc" },
    { label: "En Çok Yanıt", value: "replies_desc" },
    { label: "En Çok Görüntülenen", value: "view-count_asc" }
  ];

  const onSortChange = (event: { value: string }) => {
    const selectedValue = event.value;
    setSortOption(selectedValue);

    const [field, direction] = selectedValue.split("_");
    setSortField(field);
    setSortOrder(direction === "asc" ? 1 : -1);
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const {
    data: ForumPageResponse,
    refetch: refetchForumPage,
    isLoading: forumPageResponseLoading
  } = useQuery({
    queryKey: ["ForumPage", topicId],
    refetchOnMount: false,
    queryFn: async () => {
      if (topicId) {
        setSelectedCard(cardData[topicIdNumber - 1]);
        return forumService.GetForumByTopic(topicId);
      }
      throw new Error("Invalid topic");
    }
  });

  const formatDateTime = (dateString: Date) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Geçersiz tarih";
      }
      return date.toLocaleString("tr-TR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      console.error("Tarih formatlanırken hata oluştu:", error);
      return "Geçersiz tarih";
    }
  };

  const itemTemplate = (forum: ForumWithoutMessage, index: number) => {
    const firstLetter = forum.creator_name.charAt(0).toUpperCase();
    const bgColor = getColorForLetter(firstLetter);

    const handleClick = () => {
      navigate(`/app/forum-ayrinti/${forum.id}/${topicIdNumber}`);
    };

    const handleDelete = async (e: React.MouseEvent) => {
      try {
        confirmPopup({
          target: e.target as HTMLElement,
          message: "Bu işlem geri alınamaz. Konuyu silmek istediğinizden emin misiniz?",
          icon: "pi pi-info-circle",
          defaultFocus: "accept",
          acceptClassName: "p-button-success",
          acceptLabel: "Onayla",
          rejectLabel: "İptal",
          accept: async () => {
            const toggleVisibilityDto: ToggleVisibilityDto = { visibility: false };
            forumService.ToggleVisibility(forum.id, toggleVisibilityDto);
          },
          reject: () => {}
        });

        //refetchForumPage(); // Refresh the list after deletion
      } catch (error) {
        toast.show("Konu silinirken bir hata oluştu", "error");
      }
    };

    return (
      <div className="col-12" key={forum.id}>
        <div
          className={classNames("flex flex-row align-items-start p-2 sm:p-3 gap-3 cursor-pointer hover:surface-hover border-round transition-duration-150", {
            "border-top-1 surface-border": index !== 0
          })}
          onClick={handleClick}
        >
          {/* Başlık İkonu */}
          <div
            className="w-2.5rem h-2.5rem sm:w-3rem sm:h-3rem flex-shrink-0 flex justify-content-center align-items-center text-white text-base sm:text-xl font-bold border-circle shadow-1"
            style={{ backgroundColor: bgColor }}
          >
            {firstLetter}
          </div>

          {/* İçerik */}
          <div className="flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center flex-1 gap-2">
            {/* Sol İçerik */}
            <div className="flex flex-column gap-1 overflow-hidden">
              <div className="text-base sm:text-lg font-bold text-900 line-clamp-2">{forum.forum_header}</div>

              <div className="flex flex-wrap align-items-center gap-2 text-xs text-500">
                <span className="flex align-items-center gap-1">
                  <i className="pi pi-user text-xs"></i>
                  <span>{forum.creator_name}</span>
                </span>
                <span>•</span>
                <span className="flex align-items-center gap-1">
                  <i className="pi pi-calendar text-xs"></i>
                  <span>{forum.creation_date ? formatDateTime(forum.creation_date) : "Tarih yok"}</span>
                </span>
                <span className="sm:hidden">•</span>
                <span className="sm:hidden flex align-items-center gap-1">
                  <i className="pi pi-comments text-xs"></i>
                  <span>{forum.replies} yanıt</span>
                </span>
              </div>
            </div>

            {/* Sağ İçerik - İstatistikler & Admin Butonu */}
            <div className="flex align-items-center gap-3 flex-shrink-0">
              <div className="hidden sm:flex flex-column gap-1 align-items-end text-xs text-500">
                <div className="flex align-items-center gap-1">
                  <i className="pi pi-comments"></i>
                  <span>{forum.replies} yanıt</span>
                </div>
                <div className="flex align-items-center gap-1">
                  <i className="pi pi-eye"></i>
                  <span>{forum.view_count} görüntülenme</span>
                </div>
              </div>

              {auth.getUser().role === Role.ADMIN && (
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  rounded
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(e);
                  }}
                  tooltip="Sil"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getFilteredAndSortedForums = () => {
    if (!ForumPageResponse?.result?.data) return [];

    let filteredData = [...ForumPageResponse.result.data];

    // Arama filtrelemesi
    if (searchTerm) {
      filteredData = filteredData.filter((forum) =>
        forum.forum_header.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sıralama
    return filteredData.sort((a: any, b: any) => {
      if (sortField === "creation-date") {
        const dateA = new Date(a.creation_date).getTime();
        const dateB = new Date(b.creation_date).getTime();
        return (dateB - dateA) * sortOrder;
      } else if (sortField === "view-count") {
        return (b["view_count"] - a["view_count"]) * sortOrder;
      } else if (sortField === "replies") {
        return (b[sortField] - a[sortField]) * sortOrder;
      }
      return (a[sortField] - b[sortField]) * sortOrder;
    });
  };

  return (
    <div>
      <BreadCrumb className="mb-3 border-none p-0 bg-transparent" model={items} home={home} />
      <ConfirmPopup />

      {/* Üst Toolbar */}
      <div className="flex flex-column sm:flex-row gap-2 mb-3">
        {/* Sol Grup - Arama */}
        <div className="flex-1">
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Forum Ara..."
              className="w-full"
            />
            {searchTerm && (
              <Button
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary"
                onClick={() => setSearchTerm("")}
                tooltip="Aramayı Temizle"
              />
            )}
          </div>
        </div>

        {/* Orta Grup - Sıralama */}
        <div className="flex align-items-center">
          <Dropdown
            options={sortOptions}
            value={sortOption}
            onChange={onSortChange}
            className="w-full sm:w-15rem"
            placeholder="Sıralama Seçin"
          />
        </div>

        {/* Sağ Grup - Yeni Konu */}
        <div className="flex">
          <Button
            label="Yeni Konu Aç"
            icon="pi pi-plus"
            className="w-full sm:w-auto"
            onClick={() => navigate("/app/forum-ekle")}
          />
        </div>
      </div>

      {/* Forum Listesi */}
      <div className="card p-3 sm:p-4 mb-3">
        <Fieldset legend={selectedCard.title} className="shadow-none border-1 surface-border border-round-xl">
          <p className="text-600 text-sm sm:text-base mb-3 m-0 line-height-3">{selectedCard.description}</p>

          <DataView
            value={getFilteredAndSortedForums()}
            itemTemplate={itemTemplate as any}
            layout="list"
            paginator
            rows={10}
            emptyMessage="Listelenecek konu bulunamadı."
          />
        </Fieldset>
      </div>
    </div>
  );
};

export default ForumlarPage;
